import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faAngleDown, faGripDotsVertical, faLock, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { DraggableProvided } from "react-beautiful-dnd";
import { useIntl } from "react-intl";

import Visibility from "@mapmycustomers/shared/enum/Visibility";
import { EntityTypeSupportingPreview } from "@mapmycustomers/shared/types/entity";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Button, VisibilitySelector } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import { getCurrentUser } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { getTeams } from "@app/store/members";
import { InternalFieldDescription } from "@app/store/recordPreview/EntityColumnsConfiguration";
import { RootState } from "@app/store/rootReducer";

import messages from "../messages";
import { ALL_VISIBILITIES } from "../util/useVisibilityOptions";

import styles from "./FieldItem.module.scss";
import getVisibilityDisplayName from "./getVisibilityDisplayName";

interface Props {
  disabled?: boolean;
  entityType: EntityTypeSupportingPreview;
  fieldDescription: InternalFieldDescription;
  fields: InternalFieldDescription[];
  onChange: (
    fields: InternalFieldDescription[],
    entityType: EntityTypeSupportingPreview,
    dataChanged?: boolean
  ) => void;
  provided?: DraggableProvided;
  teams: Team[];
  user: User;
}

const FieldItem: React.FC<Props> = ({
  disabled,
  entityType,
  fieldDescription,
  fields,
  onChange,
  provided,
  teams,
  user,
}) => {
  const intl = useIntl();

  const handleVisibilityChange = useCallback(
    (fieldDescription: InternalFieldDescription) => {
      const updatedFields = fields.map((item) =>
        item.field.name === fieldDescription.field.name ? fieldDescription : item
      );
      onChange(updatedFields, entityType);
    },
    [entityType, fields, onChange]
  );

  const handleSelect = useCallback(
    (fieldDescription: InternalFieldDescription, selected: boolean) => {
      const updatedFields = fields.map((item) =>
        item.field.name === fieldDescription.field.name ? { ...fieldDescription, selected } : item
      );
      onChange(updatedFields, entityType, false);
    },
    [entityType, fields, onChange]
  );

  const handleRemove = useCallback(
    (fieldDescription: InternalFieldDescription) => {
      const removedItemIndex = fields.findIndex(
        ({ field }) => field.name === fieldDescription.field.name
      );
      const updatedFields = fields
        .filter(({ field }) => field.name !== fieldDescription.field.name)
        .map((item, index) =>
          index >= removedItemIndex ? { ...item, displayOrder: item.displayOrder - 1 } : item
        );
      onChange(updatedFields, entityType);
    },
    [entityType, fields, onChange]
  );

  return (
    <div
      className={styles.draggrable}
      {...(provided ? provided.draggableProps : {})}
      ref={provided ? provided.innerRef : undefined}
    >
      <div
        className={cn(styles.handle, { [styles.disabled]: disabled })}
        tabIndex={-1}
        {...(disabled || !provided ? {} : provided.dragHandleProps)}
      >
        <FontAwesomeIcon icon={disabled ? faLock : faGripDotsVertical} />
      </div>
      <div
        className={cn(styles.field, { [styles.disabled]: disabled })}
        key={fieldDescription.field.name}
      >
        <Checkbox
          checked={fieldDescription.selected}
          className={styles.checkbox}
          disabled={disabled}
          onChange={(checked) => handleSelect(fieldDescription, checked)}
        />
        <span className={styles.fieldName}>{fieldDescription.field.displayName}</span>
        <div className={styles.actions}>
          <VisibilitySelector
            allowedVisibilities={
              isOwner(user) ? ALL_VISIBILITIES : [Visibility.PRIVATE, Visibility.SHARED_WITH_TEAM]
            }
            disabled={disabled || !isOwner(user)}
            onChange={(visibility, selectedTeams) =>
              handleVisibilityChange({
                ...fieldDescription,
                teamIds: selectedTeams,
                visibility,
              })
            }
            selectedTeams={fieldDescription.teamIds}
            subLabel={intl.formatMessage(messages.pinFor)}
            teams={teams}
            userRole={user.role.key}
            value={fieldDescription.visibility}
          >
            <Tooltip
              title={
                disabled && fieldDescription.visibility !== Visibility.PRIVATE
                  ? intl.formatMessage(messages.setByAdmin)
                  : undefined
              }
            >
              <div>
                <Button
                  disabled={disabled || !isOwner(user)}
                  rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
                  type="control"
                >
                  {getVisibilityDisplayName(
                    intl,
                    fieldDescription.visibility,
                    user.role.key,
                    fieldDescription.teamIds?.length
                  )}
                </Button>
              </div>
            </Tooltip>
          </VisibilitySelector>
          <Button disabled={disabled} onClick={() => handleRemove(fieldDescription)} type="control">
            <FontAwesomeIcon className={styles.remove} icon={faXmark} size="sm" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
  user: getCurrentUser(state)!,
});

export default connect(mapStateToProps)(FieldItem);
